import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import Loading from 'modules/core/components/Loading'
import PanesHeader from 'modules/core/components/PanesHeader'
import PanesPadForm from 'modules/core/components/PanesPadForm'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import i18n from 'modules/core/lib/i18n'
import validateNumberInput from 'modules/core/lib/validateNumberInput'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { FamilySituations } from 'modules/employees/store/collections/familySituations'
import Employee from 'modules/employees/store/models/Employee'

interface Props {
  employee: Employee
  readonly: boolean
  legalEntity: LegalEntity | null
}

class FamilySituationForm extends Form {
  constructor(values: Record<string, unknown>) {
    super(values, {
      civil_status: 'string',
      number_of_dependants: 'number',
    })
  }
}

export const FamilySituation = observer(({ employee, readonly, legalEntity }: Props) => {
  const familySituations = useLazyRef<FamilySituations>(() => new FamilySituations()).current

  const [familySituation, setFamilySituation] = React.useState(
    familySituations.findByEmployee(employee)
  )
  const form = React.useMemo(
    () =>
      new FamilySituationForm({
        civil_status: familySituation.attributes.civil_status,
        number_of_dependants: familySituation.attributes.number_of_dependants,
      }),
    [familySituation]
  )

  const familySituationOptions = [
    {
      label: i18n.t('employee_form.family_situation.options.single'),
      value: Employee.FAMILY_SITUATION.single,
    },
    {
      label: i18n.t('employee_form.family_situation.options.cohabitating'),
      value: Employee.FAMILY_SITUATION.cohabitating,
    },
    {
      label: i18n.t('employee_form.family_situation.options.divorced'),
      value: Employee.FAMILY_SITUATION.divorced,
    },
    {
      label: i18n.t('employee_form.family_situation.options.married'),
      value: Employee.FAMILY_SITUATION.married,
    },
    {
      label: i18n.t('employee_form.family_situation.options.unknown'),
      value: Employee.FAMILY_SITUATION.unknown,
    },
    {
      label: i18n.t('employee_form.family_situation.options.civil_partnership'),
      value: Employee.FAMILY_SITUATION.civilPartnership,
    },
    {
      label: i18n.t('employee_form.family_situation.options.separated'),
      value: Employee.FAMILY_SITUATION.separated,
    },
    {
      label: i18n.t('employee_form.family_situation.options.widow'),
      value: Employee.FAMILY_SITUATION.widow,
    },
    {
      label: i18n.t('employee_form.family_situation.options.not_applicable'),
      value: Employee.FAMILY_SITUATION.notApplicable,
    },
  ]

  const { loading } = useUnsafeAsyncData(async () => {
    await familySituations.fetch({
      data: {
        employee_id: employee.id,
      },
    })
    setFamilySituation(familySituations.findByEmployee(employee))
  }, [employee])

  const onSubmit = async () => {
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: formGetDirtyFields(form),
      legal_entity_country: employeeLegalEntity(employee).country,
      layout_version: 'v1',
    })
    asyncAction(() => form.save(familySituation))
  }

  if (loading) return <Loading />

  if (legalEntity && legalEntity.country !== 'fr') return null

  return (
    <FormComponent>
      <FormRow>
        <Select
          label={i18n.t('employee_form.family_situation.family_situation')}
          options={familySituationOptions}
          field={form.get('civil_status')}
          readonly={readonly}
          none={i18n.t('common.choose')}
        />
      </FormRow>
      <FormRow>
        <Text
          label={i18n.t('employee_form.family_situation.number_of_dependents')}
          field={form.get('number_of_dependants')}
          readonly={readonly}
          validation={validateNumberInput}
        />
      </FormRow>
      {form.isDirty && (
        <FormRow footer>
          <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
        </FormRow>
      )}
    </FormComponent>
  )
})

const FamilySituationWrapper = (props: Props) => {
  if (props.legalEntity && props.legalEntity.country !== 'fr') return null

  return (
    <PanesPadForm direction="horizontal" boxed>
      <PanesHeader
        description={i18n.t('employee_form.family_situation.description')}
        illustration="user-male-female"
        title={i18n.t('employee_form.family_situation.title')}
      />
      <FamilySituation {...props} />
    </PanesPadForm>
  )
}

export default observer(FamilySituationWrapper)
